import { GameManager } from "../../GameManager";
import { BackgroundContainer } from "./BackgroundContainer";
import { BoardContainer } from "./BoardContainer";
import { BoardManager, BoardPhaseSnapshot } from "./BoardManager";
import { GUI } from "./GUI";
import { HUD } from "./hud/HUD";
import { BiddingMenu } from "./bidding-menu/BiddingMenu";
import { DevelopmentMenu } from "./gui/DevelopmentMenu";
import { GameConstants } from "../../GameConstants";
import { GameVars } from "../../GameVars";
import { BoardPhase, GameEventType, GameType } from "../../Types";
import { DevelopmentSeats } from "./development/DevelopmentContract";
import { SEATS, type Seat } from "@ravalmatic/bridge-engine/core";
import { ContractResult } from "./ContractResult";
import { AvatarSelectionLayer } from "./AvatarSelectionLayer";
import { PersistentDataManager } from "../../PersistentDataManager";
import { GameOverMenu } from "./game-over-menu/GameOverMenu";
import { getPlayerIdentity } from "./PlayerSeats";

export class BoardScene extends Phaser.Scene {

    private backgroundContainer: BackgroundContainer;
    private boardContainer: BoardContainer;
    private avatarSelectionLayer?: AvatarSelectionLayer;
    private boardManager: BoardManager;
    private hud: HUD;
    private biddingMenu: BiddingMenu;
    private gui: GUI;
    private developmentMenu?: DevelopmentMenu;
    private contractResult?: ContractResult;
    private gameOverMenu?: GameOverMenu;
    private developmentSeats?: DevelopmentSeats;
    private endDevelopmentBidding = false;

    constructor() {

        super("BoardScene");
    }

    public init(data: { boardNumber?: number; developmentSeats?: DevelopmentSeats; endBidding?: boolean }): void {

        if (data.boardNumber === undefined) {
            GameVars.matchState = GameVars.createMatchState();
        } else {
            GameVars.matchState.roundNumber = data.boardNumber;
        }
        const selection = data.developmentSeats ?? GameVars.gameData?.developmentPreferences;
        this.developmentSeats = GameConstants.DEVELOPMENT && selection
            && SEATS.includes(selection.dealer) && SEATS.includes(selection.declarer)
            ? { ...selection } : undefined;
        this.endDevelopmentBidding = GameConstants.DEVELOPMENT && data.endBidding === true;
    }

    public create(): void {

        GameManager.setCurrentScene(this);
        
        this.boardManager = new BoardManager(GameVars.matchState.roundNumber, this.developmentSeats);
        GameVars.eventsDispatcher.addEventListener(
            GameEventType.END_BIDDING_REQUESTED, this.onEndBiddingRequested, this
        );

        this.backgroundContainer = new BackgroundContainer(this);
        this.add.existing(this.backgroundContainer);

        this.boardContainer = new BoardContainer(
            this,
            this.boardManager,
            done => this.hideBiddingMenu(done),
            done => this.backgroundContainer.restoreAnimated(done),
            () => this.hud.startPlayLayoutTransition()
        );
        this.boardContainer.setAvatarClickHandler(() => this.showAvatarSelectionLayer());
        this.add.existing(this.boardContainer);
        this.hud = new HUD(this, this.boardManager);
        this.add.existing(this.hud);
        this.gui = new GUI(this, this.boardManager);
        this.add.existing(this.gui);

        if (GameConstants.DEVELOPMENT) {
            const selection = this.developmentSeats ?? {
                dealer: this.boardManager.getState().deal.dealer,
                declarer: 2,
            };
            this.developmentMenu = new DevelopmentMenu(this, selection, developmentSeats => {
                this.scene.restart({
                    boardNumber: GameVars.matchState.roundNumber, developmentSeats, endBidding: true
                });
            }, outcome => this.boardManager.forceContractResult(outcome));
            this.add.existing(this.developmentMenu);
        }
        const unsubscribeFromPhase = this.boardManager.subscribeToPhase(this.onPhaseChanged.bind(this));

        this.time.delayedCall(500, () => {
            this.showBiddingMenu();
            if (this.endDevelopmentBidding && this.developmentSeats) {
                this.boardManager.getBiddingManager().finish();
            } else {
                this.boardManager.getBiddingManager().start();
            }
        });

        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
            GameVars.eventsDispatcher.removeEventListener(
                GameEventType.END_BIDDING_REQUESTED, this.onEndBiddingRequested, this
            );
            this.developmentMenu = undefined;
            this.contractResult = undefined;
            this.gameOverMenu = undefined;
            this.hideAvatarSelectionLayer();
            unsubscribeFromPhase();
            this.boardManager.destroy();
        });
    }

    public relayout(): void {

        this.backgroundContainer?.relayout();
        this.boardContainer?.relayout();
        this.biddingMenu?.relayout();
        this.gui?.relayout();
        this.hud?.relayout();
        this.developmentMenu?.relayout();
        this.contractResult?.relayout();
        this.gameOverMenu?.relayout();
        if (this.avatarSelectionLayer) {
            this.hideAvatarSelectionLayer();
            this.showAvatarSelectionLayer();
        }
    }

    public showAvatarSelectionLayer(): void {

        if (this.avatarSelectionLayer) return;
        this.avatarSelectionLayer = new AvatarSelectionLayer(
            this, GameVars.gameData.playerAvatarId ?? 3,
            avatarId => {
                PersistentDataManager.updateGameData("playerAvatarId", avatarId);
                this.boardContainer.setPlayerAvatarId(avatarId);
                this.biddingMenu?.setPlayerAvatarId(avatarId);
                this.hideAvatarSelectionLayer();
            },
            () => this.hideAvatarSelectionLayer()
        );
        this.add.existing(this.avatarSelectionLayer);
    }

    public hideAvatarSelectionLayer(): void {

        this.avatarSelectionLayer?.destroy();
        this.avatarSelectionLayer = undefined;
    }

    private onEndBiddingRequested(): void {

        if (GameConstants.DEVELOPMENT
            && this.boardManager.getPhaseSnapshot().phase === BoardPhase.BIDDING) {
            this.showBiddingMenu();
            this.boardManager.getBiddingManager().finish();
        }
    }

    private onPhaseChanged(snapshot: BoardPhaseSnapshot): void {

        this.developmentMenu?.setPhase(snapshot.phase);
        if (snapshot.phase === BoardPhase.RESULT) {
            if (!snapshot.contractResult) {
                throw new Error("Contract result phase requires a result");
            }
            if (!this.contractResult) {
                this.contractResult = new ContractResult(
                    this, snapshot.contractResult, () => this.boardManager.completeContractResult(),
                    this.boardManager.matchManager.getProgress().finished
                );
                this.add.existing(this.contractResult);
            }
        }
        if (snapshot.phase === BoardPhase.FINISHED) {
            const manager = this.boardManager.matchManager;
            const match = manager.state;
            const progress = manager.getProgress();
            if (progress.finished === true) {
                if (!this.gameOverMenu) {
                    this.contractResult?.destroy();
                    this.contractResult = undefined;
                    this.hideBiddingMenu();
                    this.boardContainer.setVisible(false);
                    this.hud.setVisible(false);
                    const player = (seat: Seat) => {
                        const identity = getPlayerIdentity(seat, GameVars.gameData.playerAvatarId);
                        return {
                            avatarId: identity.avatarId,
                            name: GameConstants.BOT_PROFILES.find(profile => profile.id === identity.profileId)?.name
                                ?? ["NORTH", "EAST", "SOUTH", "WEST"][seat]
                        };
                    };
                    const common = {
                        scores: {
                            northSouth: { ...match.northSouth }, eastWest: { ...match.eastWest }
                        },
                        players: { 0: player(0), 1: player(1), 2: player(2), 3: player(3) }
                    };
                    if (manager.gameType === GameType.RUBBER) {
                        if (progress.winningPartnership === null) {
                            throw new Error("A completed rubber must have a winning partnership");
                        }
                        this.gameOverMenu = new GameOverMenu(this, {
                            ...common, gameType: GameType.RUBBER,
                            winningPartnership: progress.winningPartnership, games: [...match.rubber.games]
                        });
                    } else {
                        this.gameOverMenu = new GameOverMenu(this, {
                            ...common, gameType: GameType.CHICAGO,
                            winningPartnership: progress.winningPartnership, hands: [...match.hands]
                        });
                    }
                    this.add.existing(this.gameOverMenu);
                }
                return;
            }
            GameVars.matchState.roundNumber = progress.nextHand;
            this.scene.restart({ boardNumber: GameVars.matchState.roundNumber });
        }
    }

    public showBiddingMenu(): void {

        const phase = this.boardManager.getPhaseSnapshot().phase;
        if (this.biddingMenu || (phase !== BoardPhase.BIDDING && phase !== BoardPhase.CONTRACT)) {
            return;
        }

        this.biddingMenu = new BiddingMenu(this, this.boardManager);
        this.biddingMenu.once(Phaser.GameObjects.Events.DESTROY, () => {
            this.biddingMenu = undefined;
        });
        this.add.existing(this.biddingMenu);
    }

    public hideBiddingMenu(onComplete?: () => void): void {

        if (onComplete) {
            if (this.biddingMenu) {
                this.biddingMenu.hideAnimated(onComplete);
            } else {
                onComplete();
            }
        } else {
            this.biddingMenu?.destroy();
            this.biddingMenu = undefined;
        }
    }
}
